/**
 * Room detail — view + manage one room: identity/status, pairing/setup-code
 * (capability-gated), paired devices, and calendar-link state. A 2×2 card grid
 * under a header, rendered in Oxygen. The calendar card self-loads as a secondary
 * fetch.
 */

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CopyToClipboard } from '@8x8/oxygen-copy-to-clipboard';
import { CopyIcon } from '@8x8/oxygen-icon';
import Button from '@8x8/oxygen-button';
import type { Device, Room, RoomHealth } from '@spaces/contracts';
import type { SessionState } from '../api';
import { getRoom, getRoomHealth } from '../api';
import { useToast } from '../toast';
import { fmtTime } from '../ui/format';
import { Crumbs } from '../ui/Crumbs';
import { Card, HealthIndicator, Mono, Muted, Notice, PageTitle, Skel, StatusTag } from '../ui/primitives';
import { IdentityCard } from './detail/IdentityCard';
import { PairingCard } from './detail/PairingCard';
import { PreviewCard } from './detail/PreviewCard';
import { DevicesCard } from './detail/DevicesCard';
import { CalendarCard } from './detail/CalendarCard';

const Page = styled.div`
    padding: 16px 24px 40px;
    /* Cap the content so the layout doesn't sprawl edge-to-edge on ultra-wide screens
       (the cards stay a comfortable reading width; centered in the viewport). */
    max-width: 1200px;
    margin: 0 auto;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
`;

const Meta = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 8px;
    margin-bottom: 20px;
    font-size: 13px;
    color: ${p => p.theme.textColor05};
    flex-wrap: wrap;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 920px) {
        grid-template-columns: 1fr;
    }
`;

export function RoomDetail({ roomId, session }: { roomId: string; session: SessionState }): JSX.Element {
    const toast = useToast();
    const [room, setRoom] = useState<Room | null>(null);
    const [devices, setDevices] = useState<Device[]>([]);
    const [health, setHealth] = useState<RoomHealth | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const reqId = useRef(0);

    useEffect(() => {
        const id = ++reqId.current;

        setLoading(true);
        setError(null);

        void getRoom(roomId).then(result => {
            if (id !== reqId.current) {
                return;
            }
            setLoading(false);
            if (result.ok) {
                setRoom(result.data.room);
                setDevices(result.data.devices);
                // Live health is a secondary, tenant-scoped fetch (keyed off the room's tenant).
                void getRoomHealth(roomId, result.data.room.tenant).then(h => {
                    if (id === reqId.current && h.ok) {
                        setHealth(h.data);
                    }
                });
            } else {
                setError(result.message);
            }
        });
    }, [roomId, reloadKey]);

    if (loading) {
        return (
            <Page>
                <Crumbs items={[{ label: 'Rooms', to: '/rooms' }, { label: 'Loading…' }]} />
                <Skel w="280px" h={34} />
                <div style={{ height: 24 }} />
                <Grid>
                    {[0, 1, 2, 3].map(i => (
                        <Card key={i}>
                            <Skel w="40%" h={18} />
                            <div style={{ height: 16 }} />
                            <Skel w="100%" h={40} />
                            <div style={{ height: 12 }} />
                            <Skel w="60%" h={40} />
                        </Card>
                    ))}
                </Grid>
            </Page>
        );
    }

    if (error || !room) {
        return (
            <Page>
                <Crumbs items={[{ label: 'Rooms', to: '/rooms' }, { label: 'Room' }]} />
                <Notice kind="warning">Couldn&apos;t load room: {error ?? 'not found'}</Notice>
                <div style={{ marginTop: 12 }}>
                    <Button variant="secondary" onClick={() => setReloadKey(k => k + 1)}>Retry</Button>
                </div>
            </Page>
        );
    }

    return (
        <Page>
            <Crumbs items={[{ label: 'Rooms', to: `/rooms?tenant=${encodeURIComponent(room.tenant)}` }, { label: room.name }]} />

            <HeaderRow>
                <PageTitle>{room.name}</PageTitle>
                <StatusTag status={room.status} />
                {health ? <HealthIndicator health={health} /> : null}
            </HeaderRow>
            <Meta>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    id: <Mono>{room.id}</Mono>
                    <CopyToClipboard textToCopy={room.id} onCopySuccess={() => toast.notify('success', 'Room id copied.')}>
                        <CopyIcon size={14} />
                    </CopyToClipboard>
                </span>
                <span>tenant: <Muted>{room.tenant}</Muted></span>
                {room.controlPlaneUrl ? <span>control-plane: <Muted>{room.controlPlaneUrl}</Muted></span> : null}
                <span>updated {fmtTime(room.updatedAt)}</span>
            </Meta>

            <Grid>
                <IdentityCard room={room} onSaved={setRoom} />
                <PairingCard room={room} canReset={Boolean(session.capabilities?.resetPairingCode)} />
                <PreviewCard room={room} canPreview={Boolean(session.capabilities?.resetPairingCode)} />
                <DevicesCard devices={devices} />
                <CalendarCard roomId={room.id} />
            </Grid>
        </Page>
    );
}
